<template>
  <div class="x-page-container">
    <!-- Welcome Modal -->
    <MakerCartGuideModal
      v-if="showWelcome"
      v-model="showWelcomeModal"
      @close="closeWelcomeModal"
    >
      <div
        v-if="!isMobile"
        style="display: flex; flex-direction: column; gap: 32px; width: 100%"
      >
        <div class="welcome-modal__content">
          <div class="welcome-modal__text">
            <div class="welcome-modal__title-row">
              <img
                :src="welcomeImage"
                height="24px"
                alt="MakerCart Educator's Guide Header"
              />
              <h3 class="welcome-modal__title">{{ welcomeTitle }}</h3>
            </div>
            <p class="welcome-modal__description">
              {{ welcomeDescription }}
            </p>
          </div>
        </div>
        <div class="welcome-modal__actions">
          <ActionButton
            variant="primary"
            @click="closeWelcomeModal"
            class="welcome-modal__button"
          >
            Confirm
          </ActionButton>
        </div>
      </div>

      <template v-if="isMobile">
        <div class="welcome-sheet__body">
          <div class="welcome-sheet__title-row">
            <img
              :src="welcomeImage"
              height="40px"
              alt="MakerCart Educator's Guide Header"
            />
            <h3 class="welcome-sheet__title">{{ welcomeTitle }}</h3>
          </div>
          <p class="welcome-sheet__description">
            {{ welcomeDescription }}
          </p>
        </div>
        <button
          class="welcome-sheet__confirm"
          @click="closeWelcomeModal"
          style="margin-top: 16px"
        >
          Confirm
        </button>
      </template>
    </MakerCartGuideModal>

    <!-- Hero Section -->
    <div class="x-hero-section">
      <img
        v-if="heroImage"
        class="x-hero-bg"
        :src="heroImage"
        :alt="pageTitle"
      />
      <div class="x-hero-overlay"></div>
      <div class="x-hero-text">
        <h1>{{ pageTitle }}</h1>
      </div>
    </div>

    <!-- Mobile Sticky Navigation Bar -->
    <div class="x-mobile-sticky-nav">
      <div class="x-mobile-sticky-nav__inner">
        <FloatingMenu />
        <div class="x-mobile-nav-arrows">
          <button
            class="x-mobile-nav-btn"
            @click="goToPrevious"
            :disabled="!previousUrl"
            aria-label="Previous page"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#003D6A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            class="x-mobile-nav-btn"
            @click="goToNext"
            :disabled="!nextUrl"
            aria-label="Next page"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="#003D6A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="x-main-content">
      <!-- Left Menu (Desktop sidebar - hidden on mobile) -->
      <div class="x-desktop-menu">
        <FloatingMenu />
      </div>

      <!-- Right Content -->
      <div class="x-right-content">
        <!-- Navigation Buttons (Desktop) -->
        <div class="x-nav-buttons x-nav-desktop">
          <button
            class="x-nav-btn"
            @click="goToPrevious"
            :disabled="!previousUrl"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#003D6A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Previous
          </button>
          <button class="x-nav-btn" @click="goToNext" :disabled="!nextUrl">
            {{ isLastStep ? "Finish" : "Next" }}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="#003D6A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Slot for page-specific content -->
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineProps } from "vue";
import { useRouter } from "vue-router";
import FloatingMenu from "@/components/maker-cart/FloatingMenu.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import MakerCartGuideModal from "@/components/maker-cart/MakerCartGuideModal.vue";
import "@/assets/maker-cart-guide.css";

const props = defineProps({
  pageTitle: {
    type: String,
    required: true,
  },
  heroImage: {
    type: String,
    default: "/images/maker-cart-guide/teacher-presenting.png",
  },
  previousUrl: {
    type: String,
    default: null,
  },
  nextUrl: {
    type: String,
    default: null,
  },
  isLastStep: {
    type: Boolean,
    default: false,
  },
  showWelcome: {
    type: Boolean,
    default: false,
  },
  welcomeImage: {
    type: String,
    default: "/images/maker-cart-guide/maker-cart-guide.svg",
  },
  welcomeTitle: {
    type: String,
    default: "Makercart Educator's Guide",
  },
  welcomeDescription: {
    type: String,
    default:
      "Congratulations on the purchase of your new Makercart! This guide will get you started on the ethos behind the Makercart, and how you can use it to transform learning in your classroom. Get started below - we can't wait to see what you'll accomplish with your new Cart!",
  },
});

const router = useRouter();
const WELCOME_MODAL_KEY = "makercart_welcome_seen";
const showWelcomeModal = ref(
  localStorage.getItem(WELCOME_MODAL_KEY) !== "true"
);
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

const closeWelcomeModal = () => {
  showWelcomeModal.value = false;
  localStorage.setItem(WELCOME_MODAL_KEY, "true");
  document.body.style.overflow = "";
};

const goToPrevious = () => {
  if (props.previousUrl) {
    router.push(props.previousUrl);
  }
};

const goToNext = () => {
  if (props.nextUrl) {
    router.push(props.nextUrl);
  }
};
</script>

<style scoped>
/* Hero Section */
.x-hero-section {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 0 0 64px 64px;
  background-color: #00925e;
}

.x-hero-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.x-hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 61, 106, 0),
    var(--primary-blue)
  );
}

.x-hero-text {
  position: absolute;
  left: 32px;
  top: 50%;
  transform: translateY(-50%);
  width: 361px;
}

.x-hero-text h1 {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  color: #ffffff;
  margin: 0;
}

/* Main Content */
.x-main-content {
  display: flex;
  align-items: flex-start;
  padding-left: 32px;
  width: 100%;
  gap: 0;
  margin-top: 24px;
  box-sizing: border-box;
}

.x-desktop-menu {
  margin-bottom: 24px;
}

.x-right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 32px;
  min-width: 0;
}

/* Navigation Buttons (Desktop) */
.x-nav-buttons {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  width: 100%;
}

.x-nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid var(--primary-blue);
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--primary-blue);
  cursor: pointer;
  transition: all 0.2s ease;
}

.x-nav-btn:hover:not(:disabled) {
  background-color: #f2f3fb;
}

.x-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Mobile Sticky Nav */
.x-mobile-sticky-nav {
  display: none;
}

/* Content Sections (deep selector applied to slotted content) */
:deep(.x-section) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  overflow: hidden;
  align-items: flex-start;
}

:deep(.x-image-container-flat) {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

:deep(.x-image-container-flat img) {
  max-width: 100%;
}

:deep(.x-section-content) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

:deep(.x-section-title) {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue);
  margin: 0;
}

:deep(.x-section-body) {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
}

:deep(.x-section-body p) {
  margin: 0 0 16px 0;
}

:deep(.x-section-body p:last-child) {
  margin-bottom: 0;
}

:deep(.x-section-body ul) {
  margin: 0 0 16px 24px;
  padding: 0;
}

:deep(.x-section-body li) {
  margin-bottom: 8px;
}

:deep(.x-section-body .green-bold) {
  color: #00925e;
  font-weight: 700;
}

/* Welcome Modal (Desktop) */
.welcome-modal {
  background: white;
  border-radius: 12px !important;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  box-shadow: none;
  border: none;
}

.welcome-modal__content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.welcome-modal__text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.welcome-modal__title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.welcome-modal__title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
  flex: 1;
}

.welcome-modal__description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  margin: 0;
  letter-spacing: 0.08px;
}

.welcome-modal__actions {
  display: flex;
  width: 100%;
}

.welcome-modal__button {
  flex: 1;
  height: 56px;
}

/* ============ MOBILE WELCOME BOTTOM SHEET ============ */
.welcome-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.welcome-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 28px 28px 0 0;
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 24px 16px;
  overflow-y: auto;
}

.welcome-sheet__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 0;
  border-bottom: 1px solid #d0d5dd;
}

.welcome-sheet__close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
}

.welcome-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.welcome-sheet__title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.welcome-sheet__title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
  flex: 1;
}

.welcome-sheet__description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #3b4648;
  margin: 0;
}

.welcome-sheet__confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: var(--primary-blue);
  border: none;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  color: #ffffff;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease;
}

.welcome-sheet__confirm:hover {
  background-color: #012a47;
}

/* ============ TRANSITIONS ============ */
.welcome-fade-enter-active,
.welcome-fade-leave-active {
  transition: opacity 0.3s ease;
}

.welcome-fade-enter-from,
.welcome-fade-leave-to {
  opacity: 0;
}

.welcome-slide-enter-active,
.welcome-slide-leave-active {
  transition: transform 0.3s ease;
}

.welcome-slide-enter-from,
.welcome-slide-leave-to {
  transform: translateY(100%);
}

/* ============ MOBILE RESPONSIVE ============ */
@media (max-width: 768px) {
  .x-hero-section {
    height: 124px;
    border-radius: 0;
  }

  .x-page-container {
    align-items: stretch;
  }

  .x-hero-text {
    left: 16px;
    width: auto;
    max-width: calc(100% - 32px);
  }

  .x-hero-text h1 {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .x-main-content {
    flex-direction: column;
    padding-left: 16px;
    padding-right: 16px;
    margin-top: 0;
    margin-bottom: 42px;
    width: 100%;
  }

  .x-desktop-menu {
    display: none;
  }

  .x-right-content {
    padding: 0;
    margin-top: 28px;
    width: 100%;
  }

  .x-nav-desktop {
    display: none;
  }

  /* Mobile Sticky Nav Bar */
  .x-mobile-sticky-nav {
    display: block;
    position: sticky;
    top: 0;
    z-index: 50;
    background: #ffffff;
    padding: 8px 16px;
    width: 100%;
    box-sizing: border-box;
  }

  .x-mobile-sticky-nav__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .x-mobile-nav-arrows {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .x-mobile-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid var(--primary-blue);
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .x-mobile-nav-btn:hover:not(:disabled) {
    background-color: #f2f3fb;
  }

  .x-mobile-nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Mobile typography adjustments */
  :deep(.x-section-title) {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  :deep(.x-section-body) {
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.035px;
  }

  :deep(.x-section img) {
    width: 40px;
    height: 40px;
  }

  .welcome-modal {
    padding: 20px;
    gap: 24px;
  }
}
</style>
